import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { CustomerDepartmentStore, CustomerStore, ICustomerDepartment } from '@engineering11/customer-web'
import { E11ModalGlobalService } from '@engineering11/ui-lib/e11-modal'
import { isNotNil } from '@engineering11/utility'
import { filter, map } from 'rxjs'

/** What sdk-form-department emits: id and customerKey are absent on a new department. */
type EditableDepartment = Omit<ICustomerDepartment, 'id' | 'customerKey'> & Partial<Pick<ICustomerDepartment, 'id' | 'customerKey'>>

/**
 * Renders the department list itself rather than through the SDK's sdk-customer-departments, so it can share the
 * Manage Openings table system with the other settings lists. The SDK's store and edit form are still used.
 */
@Component({
  template: `
    <header class="settings-header">
      <div class="settings-header-main">
        <h2 class="settings-title">{{ "Manage your company's departments" | translate }}</h2>
        <p class="settings-subtitle">{{ 'These are used to assign specific departments to openings.' | translate }}</p>
      </div>
      <div class="settings-actions">
        <e11-button
          id="submit_account"
          value="{{ 'New Department' | translate }}"
          [icon]="'add'"
          [iconPosition]="'left'"
          [iconSize]="'md-18'"
          [size]="'sm'"
          [color]="'primary-accent'"
          [type]="'button'"
          (click)="addClicked()"
        >
        </e11-button>
      </div>
    </header>

    <div class="e11-mb-6">
      <e11-loader-dots
        *ngIf="(loaded$ | async) === false; else departmentList"
        [size]="'md'"
        [color]="'e11-bg-skin-primary-accent'"
      ></e11-loader-dots>

      <ng-template #departmentList>
        <ng-container *ngrxLet="departments$ as departments">
          <div *ngIf="departments.length; else emptyState" class="settings-table-wrap">
            <table class="settings-table">
              <thead>
                <tr>
                  <th scope="col">{{ 'Department' | translate }}</th>
                  <th scope="col" class="col-fit">{{ 'Code' | translate }}</th>
                  <th scope="col" class="col-fit">{{ 'Status' | translate }}</th>
                  <th scope="col" class="col-fit">
                    <span class="e11-sr-only">{{ 'Edit' | translate }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let department of departments; trackBy: trackById" [class.is-inactive]="!department.active" (click)="select(department)">
                  <td>
                    <div class="row-name">{{ department.name }}</div>
                  </td>
                  <td class="col-fit">
                    <span *ngIf="department.internalCode; else noCode" class="row-code">{{ department.internalCode }}</span>
                    <ng-template #noCode>—</ng-template>
                  </td>
                  <td class="col-fit">
                    <span class="status-pill" [class.is-active]="department.active"
                      ><i class="dot"></i>{{ (department.active ? 'Active' : 'Inactive') | translate }}</span
                    >
                  </td>
                  <td class="col-fit actions-cell">
                    <span class="material-icons-outlined edit-icon" aria-hidden="true">edit</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </ng-container>

        <ng-template #emptyState>
          <e11-empty-state
            [icon]="''"
            [clickable]="true"
            [border]="'solid'"
            title="{{ 'No Departments' | translate }}"
            subtitle="{{ 'It appears that we do not have any company departments entered yet.' | translate }}"
            (click)="addClicked()"
          >
          </e11-empty-state>
        </ng-template>
      </ng-template>
    </div>

    <ng-template #editDepartment>
      <div *ngrxLet="currentCustomer$ as currentCustomer">
        <sdk-form-department
          [department]="(selectedDepartment$ | async) ?? undefined"
          (cancelled)="deselect()"
          (departmentAdded)="add($event, currentCustomer.id)"
          (departmentUpdated)="update($event)"
        ></sdk-form-department>
      </div>
    </ng-template>
  `,
  styleUrls: ['./departments.component.scss'],
})
export class CustomerDepartmentsComponent implements OnInit {
  @ViewChild('editDepartment') editDepartmentModal!: TemplateRef<unknown>

  currentCustomer$ = this.customerStore.currentCustomer$.pipe(filter(isNotNil))
  loaded$ = this.departmentStore.loaded$
  selectedDepartment$ = this.departmentStore.selectedItem$

  departments$ = this.departmentStore.allItems$

  constructor(private customerStore: CustomerStore, private departmentStore: CustomerDepartmentStore, private modalService: E11ModalGlobalService) {}

  ngOnInit() {
    this.departmentStore.loadAllItems(this.currentCustomer$.pipe(map(customer => customer.id)))
  }

  trackById(_: number, department: ICustomerDepartment) {
    return department.id
  }

  addClicked() {
    this.departmentStore.selectItem(undefined)
    this.openModal()
  }

  select(department: ICustomerDepartment) {
    this.departmentStore.selectItem(department)
    this.openModal()
  }

  deselect() {
    this.departmentStore.selectItem(undefined)
    this.modalService.closeModal()
  }

  add(department: EditableDepartment, customerKey: string) {
    this.departmentStore.add({ ...department, customerKey } as ICustomerDepartment)
    this.deselect()
  }

  update(department: EditableDepartment) {
    this.departmentStore.update(department as ICustomerDepartment)
    this.deselect()
  }

  private openModal() {
    this.modalService.openModal({
      body: this.editDepartmentModal,
      width: 'lg',
      darkMode: false,
      title: 'Manage Department',
      showHeader: true,
      showFooter: false,
      hideCloseButton: false,
    })
  }
}
